/**
 * format-currency.ts — KGS price formatting for the marketplace surface
 * (STORY-003-005).
 *
 * Prices are decimal `price_kgs` `Numeric(12,2)` (frozen schema STORY-000-005;
 * realigned 2026-06-01 per ADR-031). There is NO integer-tiyin representation
 * in this codebase: the decimal value is passed STRAIGHT to
 * `Intl.NumberFormat` — no `/100`, no tiyin conversion (story Risks & gotchas;
 * REQ-PRD-§13.4-A).
 *
 * Output uses the ru-KG / ky-KG / en-KG locale so the thousands separator is a
 * (narrow no-break) space and the decimal separator is a comma in the RU/KY
 * locales — "1 234,56 KGS" (AC #5). `minimumFractionDigits: 0` drops trailing
 * `,00` on whole-number prices while `maximumFractionDigits: 2` preserves the
 * two-decimal `Numeric(12,2)` precision when present.
 *
 * No PII travels here — prices are public catalog data.
 */

/** Currency-format locales the marketplace supports (mirror SUPPORTED_LANGUAGES). */
export type KgsLocale = "ru-KG" | "ky-KG" | "en-KG";

/** Map an i18next language tag (`"ru"`, `"ru-RU"`, `"ky"`, `"en"`) to a KGS locale. */
export function kgsLocaleForLanguage(language: string): KgsLocale {
  const lang = language.split("-")[0];
  if (lang === "ky") return "ky-KG";
  if (lang === "en") return "en-KG";
  return "ru-KG";
}

/**
 * Format a decimal KGS price for display. Pass the `price_kgs` decimal value
 * verbatim (NOT divided by 100). Returns e.g. `"1 234,56 KGS"` in ru-KG.
 *
 * The caller is responsible for handling a missing price (`price_kgs` is
 * nullable server-side) — this function formats a concrete number.
 */
export function formatKgs(priceKgs: number, locale: KgsLocale): string {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "KGS",
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(priceKgs);
}

/**
 * A price BAND as one currency string: `"650 – 1 000 сом"`.
 *
 * Half the catalogue sells in a band (v0052), and `price_kgs` alone is its LOW
 * end — so an admin table rendering `formatKgs(price_kgs)` states 650 for a
 * product that sells at 650–1 000. The chat card and the marketplace grid were
 * fixed first; the admin list, the admin drawer and the seller dashboard kept
 * quoting the floor, which is the same defect one screen further back.
 *
 * The currency token is written ONCE, after the pair — `Intl` would otherwise
 * repeat it and "650 сом – 1 000 сом" reads as two separate prices.
 */
export function formatKgsRange(
  lowKgs: number,
  highKgs: number,
  locale: KgsLocale,
): string {
  // The high end carries the currency; the low end is the bare figure, so the
  // pair reads as one price rather than two. Same Intl locale on both sides,
  // so the grouping and decimal separators cannot drift apart.
  const high = formatKgs(highKgs, locale);
  const low = new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(lowKgs);
  return `${low} – ${high}`;
}
